import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { memo, useCallback, useDeferredValue, useMemo, useState, type JSX } from 'react';

import { HELP_PANEL_ATTR } from './dom';
import { uiText } from './text';
import type { HelpLanguage, HelpStep, PageHelp } from './types';
import { groupStepsBySection, localizedSection, localizedStep, stepMatchesQuery } from './types';

/**
 * "Jump to a step" — the table of contents behind the tour's step counter.
 *
 * A tour is a linear walk, and someone who wants the one field they do not understand should not
 * press Next twenty times to reach it. Grouped by the guide's sections because those are how the
 * screen is laid out, and searched with `stepMatchesQuery`, which reads the bodies too.
 */

const PANEL_WIDTH = 340;
const LIST_MAX_HEIGHT = 340;

/**
 * One line of the table of contents, memoised because the list is rebuilt on every keystroke of
 * the search above it. It localises its own title, so that work also sits behind the memo.
 */
const JumpRow = memo(
  ({
    step,
    language,
    /** The step's place in the tour — the number the reader sees, and what `onPick` reports. */
    index,
    current,
    onPick,
  }: {
    step: HelpStep;
    language: HelpLanguage;
    index: number;
    current: boolean;
    onPick: (index: number) => void;
  }): JSX.Element => (
    <ButtonBase
      onClick={() => onPick(index)}
      aria-current={current ? 'step' : undefined}
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        textAlign: 'left',
        gap: 1,
        px: 0.75,
        py: 0.6,
        borderRadius: 1,
        bgcolor: current ? 'rgba(35, 85, 146, 0.10)' : undefined,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Typography
        noWrap
        sx={{
          fontSize: 12.5,
          fontWeight: current ? 700 : 500,
          color: current ? 'primary.main' : 'text.primary',
        }}
      >
        {localizedStep(step, language).title}
      </Typography>
      <Typography sx={{ fontSize: 11, color: 'text.disabled', flexShrink: 0 }}>{index + 1}</Typography>
    </ButtonBase>
  ),
);
JumpRow.displayName = 'JumpRow';

interface HelpStepJumpProps {
  anchorEl: HTMLElement | null;
  /** The tour's steps, in tour order — indices into this array are what `onPick` returns. */
  steps: HelpStep[];
  /** The guide — for the section order and the translated section names. */
  help: PageHelp;
  language: HelpLanguage;
  currentIndex: number;
  onPick: (index: number) => void;
  onClose: () => void;
}

export const HelpStepJump = ({
  anchorEl,
  steps,
  help,
  language,
  currentIndex,
  onPick,
  onClose,
}: HelpStepJumpProps): JSX.Element => {
  const [query, setQuery] = useState('');
  /**
   * The list may lag a frame behind the typing; the field may not. Enter is resolved against the
   * LIVE query below — committing to the previous keystroke's first match is the wrong step.
   */
  const deferredQuery = useDeferredValue(query);
  const text = uiText(language);

  const matches = useMemo(
    () => steps.filter((step) => stepMatchesQuery(step, deferredQuery)),
    [steps, deferredQuery],
  );
  const groups = useMemo(() => groupStepsBySection(help.sections, matches), [help.sections, matches]);
  /** Tour position by selector, built once per tour rather than by `indexOf` per row. */
  const positionOf = useMemo(() => new Map(steps.map((step, index) => [step.selector, index])), [steps]);

  const pick = useCallback(
    (index: number): void => {
      onPick(index);
      setQuery('');
      onClose();
    },
    [onPick, onClose],
  );

  return (
    <Popover
      open={anchorEl !== null}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      // The tour card sits at `modal + 21`; a Popover defaults to the modal layer and would open
      // UNDERNEATH the very button that summoned it.
      sx={{ zIndex: (t) => t.zIndex.modal + 25 }}
      slotProps={{
        paper: {
          sx: { width: PANEL_WIDTH, borderRadius: 2 },
          ...({ [HELP_PANEL_ATTR]: 'jump' } as Record<string, unknown>),
        },
      }}
    >
      <Box sx={{ p: 1.25, pb: 0.75 }}>
        <TextField
          fullWidth
          size="small"
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={text.jumpPlaceholder}
          data-help="help-jump-search"
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            const best = steps.findIndex((step) => stepMatchesQuery(step, query));
            if (best < 0) return;
            event.preventDefault();
            pick(best);
          }}
          slotProps={{
            htmlInput: { maxLength: 60, 'aria-label': text.jumpAria },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <Box sx={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto', px: 1.25, pb: 1.25 }}>
        {matches.length === 0 && (
          <Typography sx={{ fontSize: 12, color: 'text.disabled', px: 0.5, py: 1.5 }}>
            {text.jumpEmpty}
          </Typography>
        )}

        {groups.map((group) => (
          <Box key={group.section}>
            <Typography
              sx={{
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: 'text.disabled',
                px: 0.75,
                mt: 1,
                mb: 0.25,
              }}
            >
              {localizedSection(help, group.section, language)}
            </Typography>
            {group.steps.map((step) => {
              const index = positionOf.get(step.selector);
              // Every grouped step came out of `steps`; rendering nothing rather than defaulting to
              // 0 keeps an impossible case from becoming a row that jumps to step 1.
              if (index === undefined) return null;
              return (
                <JumpRow
                  key={step.selector}
                  step={step}
                  language={language}
                  index={index}
                  current={index === currentIndex}
                  onPick={pick}
                />
              );
            })}
          </Box>
        ))}
      </Box>

      {deferredQuery.length > 0 && matches.length > 0 && (
        <Stack sx={{ px: 2, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
            {text.jumpFooter(matches.length, steps.length)}
          </Typography>
        </Stack>
      )}
    </Popover>
  );
};
